import { useState, useMemo, useEffect } from 'react'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { FileSpreadsheet, Calendar, Eye, Copy, Share2, Trash2, FolderOpen, Search, Star, BookOpen, LayoutGrid, Trophy, ExternalLink, BarChart3, ArrowRight, Globe } from 'lucide-react'
import { SHARED_TEMPLATES } from '@/data/shared-templates'
import { CASES_DATA } from '@/data/cases'
import { BUSINESS_TOPICS } from '@/data/topics'
import type { UserBizRole } from '@/utils/user-role'
import { ROLE_CATEGORY_MAP } from '@/utils/user-role'

// 模拟报表目录结构 - 按使用场景分类
const REPORT_CATEGORIES = [
  { id: 'marketing', name: '客户营销' },
  { id: 'assessment', name: '考核跟踪' },
]

// 模拟用户保存的报表数据
const SAVED_REPORTS = [
  {
    id: '1',
    name: '对公大额存款变动',
    description: 'T+1日统计当日对公客户大额存款余额变动情况，并展示最大一笔跨行转出交易对手，助您及时了解客户资金动向',
    createdAt: '2024-01-15 14:30',
    updatedAt: '2024-01-20 09:15',
    viewCount: 12,
    source: '对公存款信息',
    categoryId: 'assessment',
  },
  {
    id: '1b',
    name: '对公大额存款变动（余额1000万以上）',
    description: '统计对公客户存款余额1000万以上的大额变动情况',
    createdAt: '2024-01-15 14:30',
    updatedAt: '2024-01-20 09:15',
    viewCount: 6,
    source: '对公存款信息',
    categoryId: 'assessment',
    config: {
      selectedFields: ['d1', 'f1', 'f3', 'f6', 'f7', 'f5', 'f11', 'f13'],
      filters: [
        { id: 'filter-date', fieldId: 'd1', operator: 'eq', value: '2026-06-10' },
        { id: 'filter-balance', fieldId: 'f6', operator: 'gte', value: '1000' },
      ],
      isPivotMode: false,
      activeReportTemplate: '1',
    },
  },
  {
    id: '2',
    name: '一年期及以下对公存款日均增量',
    description: 'T+1日统计各机构对公客户存款时点与年日均，并提供比上日、比上月、比上季、比年初的比值，您可直观了解存款时点与日均的变化情况',
    createdAt: '2024-01-10 11:20',
    updatedAt: '2024-01-18 16:45',
    viewCount: 8,
    source: '各机构对公存款信息',
    categoryId: 'assessment',
  },
  {
    id: '3',
    name: '一年期及以下个人存款日均增量',
    description: '各机构个人客户存款时点与年日均统计',
    createdAt: '2024-01-12 10:00',
    updatedAt: '2024-01-19 14:30',
    viewCount: 5,
    source: '各机构个人存款信息',
    categoryId: 'assessment',
  },
]

interface SavedReport {
  id: string
  name: string
  description: string
  createdAt: string
  updatedAt: string
  viewCount: number
  source: string
  categoryId: string
  config?: unknown
  /** 共享对象列表（undefined 表示未共享） */
  sharedTargets?: Array<{ type: string; id: string; name: string }>
}

/** 从 localStorage 读取收藏 ID 集合 */
function loadFavorites(key: string): Set<string> {
  try {
    const saved = localStorage.getItem(key)
    if (saved) {
      const parsed = JSON.parse(saved)
      if (Array.isArray(parsed)) return new Set(parsed)
    }
  } catch {
    // ignore
  }
  return new Set<string>()
}

/** 从 localStorage 读取最近访问时间 { [id]: timestamp } */
function loadLastViewed(): Record<string, number> {
  try {
    const saved = localStorage.getItem('recently_viewed')
    if (saved) {
      const parsed = JSON.parse(saved)
      if (parsed && typeof parsed === 'object') return parsed
    }
  } catch { /* ignore */ }
  return {}
}

/** 记录一次访问 */
function touchLastViewed(id: string) {
  try {
    const data = loadLastViewed()
    data[id] = Date.now()
    localStorage.setItem('recently_viewed', JSON.stringify(data))
  } catch { /* ignore */ }
}

interface MySpaceProps {
  onOpenReport: (reportId: string) => void
  onCreateNew: () => void
  onEditReport?: (reportId: string) => void
  onViewReport?: (templateId?: string) => void
  onLearnMore?: (catalogId: string) => void
  onCaseDetail?: (caseId: string) => void
  onPayrollTopicAnalysis?: () => void
  /** 跳转到业务分析中心-业务主题并激活收藏筛选 */
  onNavigateToFavoritesTopics?: () => void
  /** 跳转到业务分析中心-共享模板并激活收藏筛选 */
  onNavigateToFavoritesTemplates?: () => void
  /** 跳转到业务分析中心-经典案例并激活收藏筛选 */
  onNavigateToFavoritesCases?: () => void
  version?: string
  currentUser?: string
  userBizRole?: UserBizRole
}

export function MySpace({ onOpenReport, onCreateNew: _onCreateNew, onEditReport, onViewReport, onLearnMore, onCaseDetail, onPayrollTopicAnalysis, onNavigateToFavoritesTopics, onNavigateToFavoritesTemplates, onNavigateToFavoritesCases, version, currentUser, userBizRole }: MySpaceProps) {
  // 合并预置报表和用户保存的报表（localStorage）
  // v1.0: 仅展示对公大额存款变动（id='1'）、各机构存款情况统计（id='2'对应模板'12'）+ 用户保存的报表
  const [reports, setReports] = useState<SavedReport[]>(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('saved_reports') || '[]')
      const shared = JSON.parse(localStorage.getItem('shared_reports') || '[]')
      const sharedReportIds = new Set((shared as any[]).map((s: any) => s.reportId))
      if (Array.isArray(saved) && saved.length > 0) {
        // 只保留当前登录用户保存的报表
        const myReports = (saved as any[]).filter((s: any) => !s.createdBy || s.createdBy === currentUser)
        const userReports = myReports.map((s: any) => ({
          ...s,
          sharedTargets: sharedReportIds.has(s.id)
            ? (shared as any[]).find((sh: any) => sh.reportId === s.id)?.shareTargets || []
            : undefined,
        }))
        const allowedCategory = userBizRole ? ROLE_CATEGORY_MAP[userBizRole] : null
        const filteredUserReports = allowedCategory
          ? userReports.filter((r: any) => {
              const tpl = SHARED_TEMPLATES.find(t => t.id === r.templateId)
              return !tpl || tpl.category === allowedCategory
            })
          : userReports
        // 根据业务角色过滤内置报表
        let builtInReports = SAVED_REPORTS
        if (version === '1') {
          if (userBizRole === 'corporate') {
            // 对公用户：只显示对公报表（id='1' 和 id='2'）
            builtInReports = SAVED_REPORTS.filter(r => r.id === '1' || r.id === '1b' || r.id === '2')
          } else if (userBizRole === 'retail') {
            // 零售用户：只显示零售报表（id='3'）
            builtInReports = SAVED_REPORTS.filter(r => r.id === '3')
          } else {
            // 全部：显示所有 v1.0 报表
            builtInReports = SAVED_REPORTS.filter(r => r.id === '1' || r.id === '1b' || r.id === '2')
          }
        } else {
          // v2.0 显示全部
          if (userBizRole === 'corporate') {
            builtInReports = SAVED_REPORTS.filter(r => r.id === '1' || r.id === '1b' || r.id === '2')
          } else if (userBizRole === 'retail') {
            builtInReports = SAVED_REPORTS.filter(r => r.id === '3')
          }
        }
        return [...builtInReports, ...filteredUserReports]
      }
    } catch {
      // ignore
    }
    // 根据业务角色过滤内置报表
    let builtInReports = version === '1' ? SAVED_REPORTS.filter(r => r.id === '1' || r.id === '1b' || r.id === '2') : SAVED_REPORTS
    if (userBizRole === 'corporate') {
      builtInReports = builtInReports.filter(r => r.id === '1' || r.id === '1b' || r.id === '2')
    } else if (userBizRole === 'retail') {
      builtInReports = builtInReports.filter(r => r.id === '3')
    }
    return builtInReports.map(r => ({ ...r, sharedTargets: undefined }))
  })
  const [activeTab, setActiveTab] = useState<string>('reports')
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null)
  const [searchKeyword, setSearchKeyword] = useState('')
  const [showCopyModal, setShowCopyModal] = useState(false)
  const [showShareModal, setShowShareModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [selectedReport, setSelectedReport] = useState<typeof reports[0] | null>(null)

  /* ---- 收藏数据（与业务分析中心共享同一 localStorage） ---- */
  const topicFavoriteIds = useMemo(() => loadFavorites('topic_favorites'), [])
  const templateFavoriteIds = useMemo(() => loadFavorites('template_favorites'), [])
  const caseFavoriteIds = useMemo(() => loadFavorites('case_favorites'), [])

  /* ---- 用户共享模板（从 localStorage 加载，与 SharedTemplates 一致） ---- */
  const userSharedTemplates = useMemo(() => {
    try {
      const shared = JSON.parse(localStorage.getItem('shared_reports') || '[]')
      return (shared as any[]).map((s: any) => ({
        id: s.id,
        name: s.name,
        department: '我的共享',
        description: s.description || '',
        createdAt: s.sharedAt || '',
        views: 0,
        favorites: 0,
        category: '其他',
        scenario: '营销推动',
        scope: 'mine',
      }))
    } catch {
      return []
    }
  }, [])

  const [refreshLastViewed, setRefreshLastViewed] = useState(0)
  const allTemplatePool = useMemo(() => [...SHARED_TEMPLATES, ...userSharedTemplates], [userSharedTemplates])
  const lastViewed = useMemo(() => { void refreshLastViewed; return loadLastViewed() }, [refreshLastViewed])
  const favoriteTemplates = useMemo(() => {
    const allowedCategory = userBizRole ? ROLE_CATEGORY_MAP[userBizRole] : null
    const filtered = allowedCategory
      ? allTemplatePool.filter(t => t.category === allowedCategory)
      : allTemplatePool
    return filtered.filter(t => templateFavoriteIds.has(t.id)).sort((a, b) => (lastViewed[b.id] || 0) - (lastViewed[a.id] || 0))
  }, [allTemplatePool, templateFavoriteIds, lastViewed, userBizRole])
  const favoriteCases = useMemo(() => CASES_DATA.filter(c => caseFavoriteIds.has(c.id)).sort((a, b) => (lastViewed[b.id] || 0) - (lastViewed[a.id] || 0)), [caseFavoriteIds, lastViewed])
  const favoriteTopics = useMemo(() => BUSINESS_TOPICS.filter(t => topicFavoriteIds.has(t.id)).sort((a, b) => (lastViewed[b.id] || 0) - (lastViewed[a.id] || 0)), [topicFavoriteIds, lastViewed])

  const handleFavoriteClick = (type: string, item: { id: string }) => {
    touchLastViewed(item.id)
    setRefreshLastViewed(n => n + 1)
    if (type === 'template') {
      if (item.id === '11' || item.id === 'template-payroll') {
        onViewReport?.('11')
      } else {
        onViewReport?.(item.id)
      }
    } else if (type === 'topic') {
      const topic = favoriteTopics.find(t => t.id === item.id)
      if (item.id === 'topic-payroll') {
        onPayrollTopicAnalysis?.()
      } else if (item.id === 'topic-corp-deposit') {
        onLearnMore?.('retail-deposit')
      } else if (topic?.catalogId) {
        onLearnMore?.(topic.catalogId)
      } else {
        onLearnMore?.(item.id)
      }
    } else if (type === 'case') {
      onCaseDetail?.(item.id)
    }
  }

  // 筛选报表
  const filteredReports = reports.filter(report => {
    // 目录筛选
    if (selectedCategory) {
      if (report.categoryId !== selectedCategory) return false
    }
    // 搜索筛选
    if (searchKeyword.trim()) {
      const keyword = searchKeyword.toLowerCase()
      return report.name.toLowerCase().includes(keyword) ||
        report.description.toLowerCase().includes(keyword) ||
        report.source.toLowerCase().includes(keyword)
    }
    return true
  }).sort((a, b) => (lastViewed[b.id] || 0) - (lastViewed[a.id] || 0))

  const handleCopy = (report: typeof reports[0]) => {
    setSelectedReport(report)
    setShowCopyModal(true)
  }

  const handleShare = (report: typeof reports[0]) => {
    setSelectedReport(report)
    setShowShareModal(true)
  }

  const handleDelete = (report: typeof reports[0]) => {
    setSelectedReport(report)
    setShowDeleteModal(true)
  }

  const handleConfirmCopy = () => {
    console.log('复制报表:', selectedReport?.id)
    setShowCopyModal(false)
    setSelectedReport(null)
  }

  const handleConfirmShare = (shareTargets: Array<{ type: string; id: string; name: string }>) => {
    if (!selectedReport) return
    // 预置报表不允许共享
    if (selectedReport.id === '1' || selectedReport.id === '1b' || selectedReport.id === '2') {
      setShowShareModal(false)
      setSelectedReport(null)
      return
    }
    // 检查是否已有同名已共享报表
    try {
      const existingShared = JSON.parse(localStorage.getItem('shared_reports') || '[]')
      const duplicate = existingShared.find((s: any) => s.name === selectedReport.name && s.reportId !== selectedReport.id)
      if (duplicate) {
        // 已有同名共享报表，提示用户修改名称
        alert(`已存在同名报表「${selectedReport.name}」已共享到模板页面，请先修改报表名称后再共享。`)
        setShowShareModal(false)
        setSelectedReport(null)
        return
      }
      // 保存共享记录
      const now = new Date()
      const dateStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`
      const sharedRecord = {
        id: `shared-${Date.now()}`,
        reportId: selectedReport.id,
        name: selectedReport.name,
        description: selectedReport.description || '',
        department: '我的共享',
        shareTargets,
        sharedAt: dateStr,
        config: (selectedReport as any).config || null,
      }
      existingShared.push(sharedRecord)
      localStorage.setItem('shared_reports', JSON.stringify(existingShared))
      // 更新报表列表，标记已共享
      setReports(prev => prev.map(r =>
        r.id === selectedReport.id ? { ...r, sharedTargets: shareTargets } : r
      ))
    } catch (e) {
      console.error('共享报表失败:', e)
    }
    setShowShareModal(false)
    setSelectedReport(null)
  }

  const handleConfirmDelete = () => {
    if (!selectedReport) return
    // 预置报表（ID 为 '1' 或 '2'）不允许删除
    if (selectedReport.id === '1' || selectedReport.id === '1b' || selectedReport.id === '2') {
      setShowDeleteModal(false)
      setSelectedReport(null)
      return
    }
    // 从 localStorage 删除
    try {
      const saved = JSON.parse(localStorage.getItem('saved_reports') || '[]')
      const updated = saved.filter((r: any) => r.id !== selectedReport.id)
      localStorage.setItem('saved_reports', JSON.stringify(updated))
    } catch {
      // ignore
    }
    // 更新 UI 状态
    setReports(prev => prev.filter(r => r.id !== selectedReport.id))
    setShowDeleteModal(false)
    setSelectedReport(null)
  }

  return (
    <div className="flex-1 overflow-auto bg-gray-50">
      <div className="p-6">
        {/* 页面标题 */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-lg font-semibold text-gray-900">我的空间</h1>
            <p className="text-sm text-gray-500 mt-1">管理您保存的报表和收藏的内容</p>
          </div>
        </div>

        {/* 统计卡片 */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                  <FileSpreadsheet className="w-5 h-5 text-gcb-primary" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{reports.length}</p>
                  <p className="text-xs text-gray-500">我的报表</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                  <Star className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{version === '1' ? favoriteTemplates.length : favoriteTopics.length + favoriteTemplates.length + favoriteCases.length}</p>
                  <p className="text-xs text-gray-500">我的收藏</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                  <Eye className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">20</p>
                  <p className="text-xs text-gray-500">累计查看</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tab 切换 */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-4">
          <TabsList className="h-8 bg-gray-100 w-fit">
            <TabsTrigger value="reports" className="text-xs h-7 px-3 data-[state=active]:bg-white data-[state=active]:text-gcb-primary">
              <FileSpreadsheet className="w-3.5 h-3.5 mr-1" />我的报表
            </TabsTrigger>
            <TabsTrigger value="favorites" className="text-xs h-7 px-3 data-[state=active]:bg-white data-[state=active]:text-gcb-primary">
              <Star className="w-3.5 h-3.5 mr-1" />我的收藏
            </TabsTrigger>
          </TabsList>

          <TabsContent value="reports" className="mt-4">
            <div className="flex flex-row gap-4 items-start">
              {/* 左侧报表目录 */}
              <div className="w-48 min-w-48 bg-white rounded-lg border shadow-sm overflow-visible shrink-0">
                <div className="h-10 px-3 border-b bg-gray-50 flex items-center">
                  <FolderOpen className="w-4 h-4 text-gray-600 mr-2" />
                  <span className="text-xs font-semibold text-gray-700">报表目录</span>
                </div>
                <div className="p-2 space-y-1">
                    {/* 全部分类 */}
                    <button
                      onClick={() => setSelectedCategory(null)}
                      className={`w-full text-left px-3 py-2 rounded-md text-xs transition-colors ${
                        selectedCategory === null
                          ? 'bg-blue-50 text-gcb-primary font-medium'
                          : 'text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      📁 全部报表
                    </button>
                    {/* 按使用场景分类 */}
                    <div className="pl-4 space-y-1 border-l-2 border-gray-100 ml-2">
                      {REPORT_CATEGORIES.map(cat => (
                        <button
                          key={cat.id}
                          onClick={() => setSelectedCategory(cat.id)}
                          className={`w-full text-left px-3 py-2 rounded-md text-xs transition-colors ${
                            selectedCategory === cat.id
                              ? 'bg-blue-50 text-gcb-primary font-medium'
                              : 'text-gray-700 hover:bg-gray-100'
                          }`}
                        >
                          📁 {cat.name}
                        </button>
                      ))}
                    </div>
                  </div>
              </div>

              {/* 右侧报表列表和搜索 */}
              <div className="flex-1">
                {/* 搜索框 */}
                <Card className="mb-4">
                  <CardContent className="p-3">
                    <div className="flex items-center gap-2">
                      <Search className="w-4 h-4 text-gray-400" />
                      <Input
                        placeholder="搜索报表名称、描述、数据源..."
                        value={searchKeyword}
                        onChange={(e) => setSearchKeyword(e.target.value)}
                        className="border-0 focus-visible:ring-0 focus-visible:ring-offset-0 px-0 text-sm"
                      />
                      {searchKeyword && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setSearchKeyword('')}
                          className="h-6 px-2 text-xs"
                        >
                          清空
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* 报表列表 */}
                <Card>
                  <CardHeader className="pb-3 border-b">
                    <div className="flex items-center justify-between">
                      <h2 className="font-semibold text-gray-900 text-sm">已保存的报表</h2>
                      <span className="text-xs text-gray-500">{filteredReports.length} 个报表</span>
                    </div>
                  </CardHeader>
                  <CardContent className="p-0">
                    <ScrollArea className="h-[400px]">
                      <div className="divide-y divide-gray-100">
                        {filteredReports.length === 0 ? (
                          <div className="text-center py-12 text-gray-500 text-sm">
                            {searchKeyword ? '未找到匹配的报表' : '该目录下暂无报表'}
                          </div>
                        ) : (
                          filteredReports.map((report) => (
                            <div
                              key={report.id}
                              className="flex items-center justify-between p-4 hover:bg-gray-50 transition-colors group"
                            >
                              <div className="flex items-center gap-3 flex-1 min-w-0 cursor-pointer" onClick={() => { touchLastViewed(report.id); setRefreshLastViewed(n => n + 1); onOpenReport(report.id) }}>
                                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-100 to-blue-50 flex items-center justify-center shrink-0">
                                  <FileSpreadsheet className="w-5 h-5 text-gcb-primary" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2">
                                    <h3 className="font-medium text-sm text-gray-900 truncate">
                                      {report.name}
                                    </h3>
                                    <Badge variant="outline" className="text-[10px] h-5">
                                      {report.source}
                                    </Badge>
                                    {/* 已共享标识 */}
                                    {report.sharedTargets && report.sharedTargets.length > 0 && (
                                      <span className="relative group/shared">
                                        <Badge className="text-[10px] h-5 px-1.5 bg-green-50 text-green-700 border-green-200 cursor-default">
                                          <Globe className="w-2.5 h-2.5 mr-0.5" />
                                          已共享
                                        </Badge>
                                        {/* 悬浮浮现共享对象 */}
                                        <div className="absolute left-0 bottom-full mb-1 z-50 invisible opacity-0 group-hover/shared:visible group-hover/shared:opacity-100 transition-opacity pointer-events-none">
                                          <div className="bg-gray-800 text-white text-xs px-3 py-1.5 rounded whitespace-pre-wrap shadow-lg min-w-max max-w-[200px]">
                                            {report.sharedTargets.map((t, i) => {
                                              const label = t.type === 'all' ? '全部用户'
                                                : t.type === 'person' ? `个人：${t.name}`
                                                : t.type === 'role' ? `角色：${t.name}`
                                                : `机构：${t.name}`
                                              return <div key={i}>{label}</div>
                                            })}
                                          </div>
                                        </div>
                                      </span>
                                    )}
                                  </div>
                                  <p className="text-xs text-gray-500 mt-0.5 truncate">
                                    {report.description}
                                  </p>
                                  <div className="flex items-center gap-3 mt-1 text-xs text-gray-400">
                                    <span>更新于 {report.updatedAt}</span>
                                    <span className="flex items-center gap-1">
                                      <Eye className="w-3 h-3" />
                                      {report.viewCount}
                                    </span>
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 text-gcb-primary hover:text-gcb-primaryDark hover:bg-blue-50"
                                  onClick={() => onEditReport?.(report.id)}
                                  title="查看/编辑"
                                >
                                  <Eye className="w-3.5 h-3.5" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8"
                                  onClick={() => handleCopy(report)}
                                  title="复制报表"
                                >
                                  <Copy className="w-3.5 h-3.5" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8"
                                  onClick={() => handleShare(report)}
                                  title="分享报表"
                                >
                                  <Share2 className="w-3.5 h-3.5" />
                                </Button>
                                {/* 预置报表不显示删除按钮 */}
                                {report.id !== '1' && report.id !== '2' && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 text-red-500 hover:text-red-600 hover:bg-red-50"
                                  onClick={() => handleDelete(report)}
                                  title="删除报表"
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </Button>
                                )}
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    </ScrollArea>
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="favorites" className="mt-4">
            <div className="grid grid-cols-3 gap-4">
              {/* 收藏的业务主题 - v2.0 only */}
              {version !== '1' && (
              <Card className="overflow-hidden">
                <CardHeader className="pb-3 border-b bg-gradient-to-r from-emerald-50 to-white">
                  <div className="flex items-center gap-2">
                    <BookOpen className="w-4 h-4 text-emerald-600" />
                    <h3 className="font-semibold text-gray-900 text-sm">业务主题</h3>
                    <Badge variant="outline" className="text-[10px] h-5 ml-auto">{favoriteTopics.length}</Badge>
                    {onNavigateToFavoritesTopics && favoriteTopics.length > 0 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-5 px-1 text-[10px] text-emerald-600 hover:text-emerald-700 ml-auto"
                        onClick={(e) => { e.stopPropagation(); onNavigateToFavoritesTopics() }}
                      >
                        更多 <ArrowRight className="w-3 h-3 ml-0.5" />
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  <ScrollArea className="h-72">
                    <div className="divide-y divide-gray-50">
                      {favoriteTopics.length === 0 ? (
                        <div className="text-center py-12 text-gray-400 text-xs">暂无收藏</div>
                      ) : (
                        favoriteTopics.map((topic) => (
                          <div
                            key={topic.id}
                            className="p-3 hover:bg-emerald-50/50 cursor-pointer transition-colors group"
                            onClick={() => handleFavoriteClick('topic', topic)}
                          >
                            <div className="flex items-start gap-2">
                              <div className="w-8 h-8 rounded bg-emerald-100 flex items-center justify-center shrink-0">
                                <BarChart3 className="w-4 h-4 text-emerald-600" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <h4 className="text-xs font-medium text-gray-900 truncate group-hover:text-gcb-primary transition-colors">{topic.name}</h4>
                                <p className="text-[10px] text-gray-400 mt-0.5">{topic.department}</p>
                                <p className="text-[10px] text-gray-500 mt-1 line-clamp-1">{topic.description}</p>
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </ScrollArea>
                </CardContent>
              </Card>
              )}

              {/* 收藏的共享模板 */}
              <Card className={`overflow-hidden ${version === '1' ? 'col-span-3' : ''}`}>
                <CardHeader className="pb-3 border-b bg-gradient-to-r from-purple-50 to-white">
                  <div className="flex items-center gap-2">
                    <LayoutGrid className="w-4 h-4 text-purple-600" />
                    <h3 className="font-semibold text-gray-900 text-sm">收藏模板</h3>
                    <Badge variant="outline" className="text-[10px] h-5">{favoriteTemplates.length}</Badge>
                    {onNavigateToFavoritesTemplates && favoriteTemplates.length > 0 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-5 px-1 text-[10px] text-purple-600 hover:text-purple-700 ml-auto"
                        onClick={(e) => { e.stopPropagation(); onNavigateToFavoritesTemplates() }}
                      >
                        更多 <ArrowRight className="w-3 h-3 ml-0.5" />
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  <ScrollArea className="h-80">
                    <div className="divide-y divide-gray-50">
                      {favoriteTemplates.length === 0 ? (
                        <div className="text-center py-12 text-gray-400 text-xs">暂无收藏</div>
                      ) : (
                        favoriteTemplates.map((template) => (
                          <div
                            key={template.id}
                            className="p-3 hover:bg-purple-50/50 cursor-pointer transition-colors group"
                            onClick={() => handleFavoriteClick('template', template)}
                          >
                            <div className="flex items-start gap-2">
                              <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center shrink-0">
                                <ExternalLink className="w-5 h-5 text-purple-600" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <h4 className="text-sm font-medium text-gray-900 truncate group-hover:text-gcb-primary transition-colors">{template.name}</h4>
                                <p className="text-xs text-gray-500 mt-1 line-clamp-2">{template.description}</p>
                                <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                                  <span className="px-1.5 py-0.5 text-[10px] text-purple-600 border border-purple-200 rounded">{template.category}</span>
                                  {template.classification && (
                                    <span className="px-1.5 py-0.5 text-[10px] text-indigo-600 border border-indigo-200 rounded">{template.classification}</span>
                                  )}
                                  {template.tags?.map(t => (
                                    <span key={t} className="px-1.5 py-0.5 text-[10px] text-amber-600 border border-amber-200 rounded">{t}</span>
                                  ))}
                                  <span className="text-[10px] text-gray-400 flex items-center gap-0.5 ml-auto"><Eye className="w-2.5 h-2.5" />{template.views}</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </ScrollArea>
                </CardContent>
              </Card>

              {/* 收藏的经典案例 - v2.0 only */}
              {version !== '1' && (
              <Card className="overflow-hidden">
                <CardHeader className="pb-3 border-b bg-gradient-to-r from-amber-50 to-white">
                  <div className="flex items-center gap-2">
                    <Trophy className="w-4 h-4 text-amber-600" />
                    <h3 className="font-semibold text-gray-900 text-sm">经典案例</h3>
                    <Badge variant="outline" className="text-[10px] h-5 ml-auto">{favoriteCases.length}</Badge>
                    {onNavigateToFavoritesCases && favoriteCases.length > 0 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-5 px-1 text-[10px] text-amber-600 hover:text-amber-700 ml-auto"
                        onClick={(e) => { e.stopPropagation(); onNavigateToFavoritesCases() }}
                      >
                        更多 <ArrowRight className="w-3 h-3 ml-0.5" />
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  <ScrollArea className="h-72">
                    <div className="divide-y divide-gray-50">
                      {favoriteCases.length === 0 ? (
                        <div className="text-center py-12 text-gray-400 text-xs">暂无收藏</div>
                      ) : (
                        favoriteCases.map((c) => (
                          <div
                            key={c.id}
                            className="p-3 hover:bg-amber-50/50 cursor-pointer transition-colors group"
                            onClick={() => handleFavoriteClick('case', c)}
                          >
                            <div className="flex items-start gap-2">
                              <div className="w-8 h-8 rounded bg-amber-100 flex items-center justify-center shrink-0 text-sm">
                                📊
                              </div>
                              <div className="flex-1 min-w-0">
                                <h4 className="text-xs font-medium text-gray-900 truncate group-hover:text-gcb-primary transition-colors">{c.name}</h4>
                                <p className="text-[10px] text-gray-400 mt-0.5">{c.businessLine}</p>
                                <p className="text-[10px] text-gray-500 mt-1 line-clamp-1">{c.intro}</p>
                                <div className="flex items-center gap-2 mt-1">
                                  <span className="text-[10px] text-gray-400 flex items-center gap-0.5"><Eye className="w-2.5 h-2.5" />{c.viewCount}</span>
                                  <span className="text-[10px] text-gray-400 flex items-center gap-0.5"><Star className="w-2.5 h-2.5" />{c.likeCount}</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </ScrollArea>
                </CardContent>
              </Card>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* 复制确认弹窗 */}
      <Dialog open={showCopyModal} onOpenChange={setShowCopyModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>复制报表</DialogTitle>
            <DialogDescription>
              复制将生成一个新的报表副本，您可以修改后保存。
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-gray-700">
              确定要复制"<span className="font-medium text-gray-900">{selectedReport?.name}</span>"吗？
            </p>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowCopyModal(false)} className="h-10 px-4">
              取消
            </Button>
            <Button onClick={handleConfirmCopy} className="h-10 px-6 bg-gcb-primary hover:bg-gcb-primaryDark">
              确认复制
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 分享弹窗 */}
      <Dialog open={showShareModal} onOpenChange={setShowShareModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>分享报表</DialogTitle>
            <DialogDescription>
              选择分享对象：某个人、某个角色、某个机构或全部用户
            </DialogDescription>
          </DialogHeader>
          <ShareForm
            reportName={selectedReport?.name || ''}
            onShare={handleConfirmShare}
            onCancel={() => setShowShareModal(false)}
          />
        </DialogContent>
      </Dialog>

      {/* 删除确认弹窗 */}
      <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>删除报表</DialogTitle>
            <DialogDescription>
              删除后将无法恢复，请谨慎操作。
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-gray-700">
              确定要删除"<span className="font-medium text-gray-900">{selectedReport?.name}</span>"吗？
            </p>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowDeleteModal(false)} className="h-10 px-4">
              取消
            </Button>
            <Button onClick={handleConfirmDelete} className="h-10 px-6 bg-red-600 hover:bg-red-700">
              确认删除
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

// 分享表单组件
interface ShareFormProps {
  reportName: string
  onShare: (targets: Array<{ type: string; id: string; name: string }>) => void
  onCancel: () => void
}

function ShareForm({ reportName: _reportName, onShare, onCancel }: ShareFormProps) {
  const [shareType, setShareType] = useState<'person' | 'role' | 'org' | 'all'>('person')
  const [selectedTargets, setSelectedTargets] = useState<string[]>([])

  // 模拟数据
  const persons = [
    { id: 'p1', name: '张三' },
    { id: 'p2', name: '李四' },
    { id: 'p3', name: '王五' },
  ]
  const roles = [
    { id: 'r1', name: '风险管理角色' },
    { id: 'r2', name: '数据分析角色' },
    { id: 'r3', name: '管理报表角色' },
  ]
  const orgs = [
    { id: 'o1', name: '总行' },
    { id: 'o2', name: '广州分行' },
    { id: 'o3', name: '深圳分行' },
  ]

  const handleSubmit = () => {
    if (shareType === 'all') {
      onShare([{ type: 'all', id: 'all', name: '全部用户' }])
      return
    }
    const sourceList = shareType === 'person' ? persons : shareType === 'role' ? roles : orgs
    const targets = selectedTargets
      .map(id => sourceList.find(s => s.id === id))
      .filter(Boolean)
      .map(s => ({ type: shareType, id: s!.id, name: s!.name }))
    if (targets.length === 0) return
    onShare(targets)
  }

  const toggleTarget = (id: string) => {
    setSelectedTargets(prev =>
      prev.includes(id) ? prev.filter(t => t !== id) : [...prev, id]
    )
  }

  return (
    <div className="space-y-4 py-2">
      {/* 分享类型选择 */}
      <div className="space-y-2">
        <label className="text-sm font-medium text-gray-700">分享范围</label>
        <div className="grid grid-cols-4 gap-2">
          <Button
            variant={shareType === 'person' ? 'default' : 'outline'}
            size="sm"
            onClick={() => { setShareType('person'); setSelectedTargets([]) }}
            className={shareType === 'person' ? 'bg-gcb-primary' : ''}
          >
            个人
          </Button>
          <Button
            variant={shareType === 'role' ? 'default' : 'outline'}
            size="sm"
            onClick={() => { setShareType('role'); setSelectedTargets([]) }}
            className={shareType === 'role' ? 'bg-gcb-primary' : ''}
          >
            角色
          </Button>
          <Button
            variant={shareType === 'org' ? 'default' : 'outline'}
            size="sm"
            onClick={() => { setShareType('org'); setSelectedTargets([]) }}
            className={shareType === 'org' ? 'bg-gcb-primary' : ''}
          >
            机构
          </Button>
          <Button
            variant={shareType === 'all' ? 'default' : 'outline'}
            size="sm"
            onClick={() => { setShareType('all'); setSelectedTargets([]) }}
            className={shareType === 'all' ? 'bg-gcb-primary' : ''}
          >
            全部
          </Button>
        </div>
      </div>

      {/* 分享对象选择 */}
      {shareType !== 'all' && (
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700">
            选择{shareType === 'person' ? '个人' : shareType === 'role' ? '角色' : '机构'}
          </label>
          <div className="max-h-48 overflow-y-auto border rounded-md p-2 space-y-1">
            {(shareType === 'person' ? persons : shareType === 'role' ? roles : orgs).map(item => (
              <div
                key={item.id}
                className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded-md cursor-pointer"
                onClick={() => toggleTarget(item.id)}
              >
                <div className={`w-4 h-4 rounded border flex items-center justify-center ${
                  selectedTargets.includes(item.id) ? 'bg-gcb-primary border-gcb-primary' : 'border-gray-300'
                }`}>
                  {selectedTargets.includes(item.id) && (
                    <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </div>
                <span className="text-sm text-gray-700">{item.name}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {shareType === 'all' && (
        <div className="bg-blue-50 rounded-md p-3 text-sm text-gray-700">
          将报表分享给系统中的所有用户
        </div>
      )}

      <DialogFooter className="gap-2 pt-4">
        <Button variant="outline" onClick={onCancel} className="h-10 px-4">
          取消
        </Button>
        <Button
          onClick={handleSubmit}
          className="h-10 px-6 bg-gcb-primary hover:bg-gcb-primaryDark"
          disabled={shareType !== 'all' && selectedTargets.length === 0}
        >
          确认分享
        </Button>
      </DialogFooter>
    </div>
  )
}
